import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useMediaQuery, useTheme, Stack } from '@mui/material'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog/ActionDialog'
import { PositionedAction } from '@e3dc-react/shell/Elements/Dialog/ActionDialog/types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { VehicleSettingsFormFieldNames, VehicleSettingsFormValues } from '../../types'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { VehicleImageUpload } from '../vehicle-image'
import { useValidateVehicleSettingsForm, useUpdateVehicleSettings } from '../../hooks'
import VinTile from '../vin-tile/VinTile'

interface Props {
  onClose: () => void
  isOpen: boolean
  imageUrl: string | null
  hasCustomImage: boolean
  vehicleName: string
  vin?: string
  installationId: number
  vehicleId: number
}

const INITIAL_VALUES: VehicleSettingsFormValues = {
  customImage: null,
  customName: '',
}

/**
 * Modal to edit vehicle image and name
 */
const VehicleSettingsModal: React.FC<Props> = ({ onClose, installationId, vehicleId, isOpen, vehicleName, vin, imageUrl, hasCustomImage }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const { vehicleSettingsFormSchema } = useValidateVehicleSettingsForm()
  const updateVehicleSettings = useUpdateVehicleSettings(installationId, onClose)

  const methods = useForm<VehicleSettingsFormValues>({
    resolver: zodResolver(vehicleSettingsFormSchema),
    defaultValues: {
      ...INITIAL_VALUES,
      customName: vehicleName,
    },
    mode: 'onChange',
  })
  const onSubmit = async (data: VehicleSettingsFormValues): Promise<void> => {
    updateVehicleSettings.mutate({
      installationId,
      vehicleId,
      payload: data,
    })
  }

  const secondaryAction: PositionedAction = {
    position: '3',
    style: 'secondary',
    name: t('common.cancel'),
    onClick: onClose,
  }

  const primaryAction: PositionedAction = {
    position: '4',
    style: 'primary',
    name: t('common.save'),
    onClick: methods.handleSubmit(onSubmit),
  }

  return (
    <Dialog
      open={isOpen}
      fullScreen={!upMd}
      navigateBackButton={true}
      closeButtonVisible={false}
      onClose={onClose}
      onNavigateBack={onClose}
      title={t('vehicle-integration.settings-modal.title')}
      actions={[primaryAction, secondaryAction]}
      maxWidth="sm"
    >
      <FormProvider {...methods}>
        <Stack mt={2} gap={2}>
          <Typography variant="h3">vehicle-integration.settings-modal.vehicle-name-header</Typography>
          <TextField name={VehicleSettingsFormFieldNames.CustomName} />

          {vin && (
            <>
              <Typography variant="h3" mt={2}>
                vehicle-integration.settings-modal.vin
              </Typography>
              <VinTile vin={vin} />
            </>
          )}

          <Typography variant="h3" mt={2}>
            vehicle-integration.settings-modal.vehicle-image-header
          </Typography>
          <VehicleImageUpload initialImageUrl={imageUrl} hasCustomImage={hasCustomImage} />
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default VehicleSettingsModal
